import {tokens, view, css} from '@sail/ui';
import {createViewConfig, assignProps, dynamic, media} from '@sail/engine';
import type {View} from '@sail/engine';
import {
  ListItem,
  ListItemConfig,
} from 'mkt-ssr/components/sail/ListItem/ListItem';
import {HelmIconAsset} from '@sail/helm';

export type ListProps = View.IntrinsicElement<
  'ul',
  {
    /**
     * The ListItems to be displayed inside the List
     */
    children:
      | React.ReactElement<typeof ListItem>
      | React.ReactElement<typeof ListItem>[];
    /**
     * The number of columns in which to display the list items
     */
    columnCount?: 1 | 2 | 3;
    /**
     * The icon displayed next to each of the list items by default
     */
    defaultIcon?: HelmIconAsset;
  }
>;

// eslint-disable-next-line @stripe-internal/stripe/no-mixed-react-exports
export const ListConfig = createViewConfig({
  props: {} as ListProps,
  name: 'List',
  defaults: {
    columnCount: 1,
  },
});

/**
 * An unordered list of items (only accepts ListItem(s) as children)
 */
export const List = ListConfig.createView(
  ({children, defaultIcon, ...rest}) => {
    return <view.ul {...rest}>{children}</view.ul>;
  },
  {
    css: {
      stack: 'y',
      gap: 'xsmall',
      padding: 'space.0',
      marginY: 'xsmall',
    },
    variants: {
      columnCount: {
        2: css({
          display: 'grid',
          [media.up(tokens.viewport.small)]: {
            grid: 'auto / repeat(2, 1fr)',
            columnGap: `${tokens.space.xlarge}`,
          },
        }),
        3: css({
          display: 'grid',
          [media.up(tokens.viewport.small)]: {
            grid: 'auto / repeat(3, 1fr)',
            columnGap: `${tokens.space.xlarge}`,
          },
        }),
      },
    },
    uses: [
      dynamic((props) => {
        return [
          ListItemConfig.customize({
            uses: [
              assignProps(({icon}) => ({
                icon: icon === undefined ? props.defaultIcon : icon,
              })),
            ],
          }),
        ];
      }),
    ],
  },
);
